import React from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Loader2, X } from 'lucide-react';
import type { ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { AccentButton, QuietButton, SmallCaps } from './ShopChrome';

export interface HostedPaymentModalProps {
  theme: ShopTheme;
  /** Name shown to the guest, e.g. "Mollie". */
  providerLabel: string;
  amountLabel: string;
  /** True once the guest closed the payment window without us seeing a paid status. */
  popupClosed: boolean;
  /** True when the popup could not be opened at all (blocker, sandbox). */
  popupBlocked: boolean;
  onReopen: () => void;
  onOpenHere: () => void;
  onCancel: () => void;
}

/**
 * Hosted checkout (Mollie) opens in its own window — a provider page cannot be framed.
 * This modal is what the guest sees behind it: what is happening, and a way back in if
 * they closed the window or a blocker swallowed it.
 */
const HostedPaymentModal: React.FC<HostedPaymentModalProps> = ({
  theme,
  providerLabel,
  amountLabel,
  popupClosed,
  popupBlocked,
  onReopen,
  onOpenHere,
  onCancel,
}) => {
  const { t } = useI18n();
  const body = (
    <>
      <div
        onClick={onCancel}
        className="fixed inset-0 z-[2147483646]"
        style={{ background: 'rgba(27,26,23,.55)', backdropFilter: 'blur(3px)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed left-1/2 top-1/2 z-[2147483647] w-[calc(100%-2rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 p-7"
        style={{
          background: theme.surface,
          border: `1px solid ${theme.hairline}`,
          borderTop: `3px solid ${theme.accent}`,
          borderRadius: theme.radius,
          fontFamily: theme.fontFamily,
          boxShadow: '0 24px 60px -24px rgba(28,26,23,.45)',
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('common.close')}
          className="absolute right-4 top-4"
          style={{ color: theme.label }}
        >
          <X className="h-4 w-4" />
        </button>

        <SmallCaps theme={theme}>{t('hosted.eyebrow')}</SmallCaps>
        <h2
          style={{
            margin: '12px 0 8px',
            font: `500 24px/1.2 ${theme.fontFamily}`,
            letterSpacing: '-.02em',
            color: theme.ink,
          }}
        >
          {popupBlocked
            ? t('hosted.title_blocked')
            : popupClosed
              ? t('hosted.title_closed')
              : t('hosted.title_open', { amount: amountLabel })}
        </h2>
        <p style={{ margin: '0 0 24px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
          {popupBlocked
            ? t('hosted.body_blocked', { provider: providerLabel })
            : popupClosed
              ? t('hosted.body_closed')
              : t('hosted.body_open', { provider: providerLabel })}
        </p>

        {!popupBlocked && !popupClosed && (
          <div
            className="mb-6 flex items-center gap-3 px-4 py-3"
            style={{ background: theme.stub, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
          >
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: theme.accent }} />
            <span style={{ font: `400 14px/1.4 ${theme.fontFamily}`, color: theme.body }}>
              {t('hosted.waiting')}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          <AccentButton theme={theme} onClick={onReopen} className="w-full">
            <ExternalLink className="h-4 w-4" />
            {popupBlocked || popupClosed
              ? t('hosted.open_provider', { provider: providerLabel })
              : t('hosted.bring_back')}
          </AccentButton>
          <QuietButton theme={theme} onClick={onOpenHere} className="w-full">
            {t('hosted.pay_here')}
          </QuietButton>
          <button
            type="button"
            onClick={onCancel}
            className="pt-1"
            style={{ font: `400 13px/1.4 ${theme.fontFamily}`, color: theme.label }}
          >
            {t('hosted.cancel')}
          </button>
        </div>
      </div>
    </>
  );

  return typeof document === 'undefined' ? null : createPortal(body, document.body);
};

export default HostedPaymentModal;
